/**
 * CompletionConfirmationDialog — F-008 Phase 6 (IMPL-P6-005).
 *
 * Dialog di conferma annidato dentro `UploadRicevutaModal`. Mostra un
 * riepilogo dei valori critici (protocollo, data registrazione, stato firma)
 * prima del commit irreversibile (transizione → `COMPLETATA`).
 *
 * Pattern di riferimento (F-007 `SegnalazioneErroreModal`):
 *  - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` puntato sul titolo
 *  - `aria-describedby` puntato sulla descrizione
 *  - ESC handler → `onCancel`
 *  - Click outside backdrop → `onCancel`
 *  - Focus iniziale: bottone "Conferma" (l'azione primaria), così l'operatore
 *    può confermare con Enter
 *
 * Accessibilità WCAG 2.1 AA:
 *  - 2.4.3 Focus order: focus → conferma → annulla → backdrop
 *  - 4.1.2 Name, Role, Value: `role="dialog"` + `aria-modal` + label/description
 *  - 1.3.1 Info & Relationships: tabella riepilogo con `<dt>/<dd>`
 *  - 3.3.4 Error prevention: dialog di conferma esplicito prima di azione irreversibile
 */
import * as React from 'react';
import { Button } from '@/components/ui/button';

export interface CompletionConfirmationDialogProps {
  /** Mostra/nasconde il dialog. */
  open: boolean;
  /** Short ID della pratica (es. ultimi 8 char del cuid) per il riepilogo. */
  praticaShortId: string;
  /** Nome/etichetta del cliente; vuoto se non disponibile. */
  clientName?: string;
  /** Numero di protocollo AdE da confermare. */
  protocolloAde: string;
  /** Data di registrazione (ISO `YYYY-MM-DD` o stringa già formattata). */
  dataRegistrazione: string;
  /** Stato firma digitale (VALID / UNRECOGNIZED / SKIPPED). */
  firmaDigitaleStatus: string;
  /** True mentre la mutation di commit è in flight. */
  isPending: boolean;
  /** Callback Conferma — chiama `useUploadRicevuta.mutate(...)` lato caller. */
  onConfirm: () => void;
  /** Callback Annulla — chiude il dialog senza committare. */
  onCancel: () => void;
}

const FIRMA_LABELS: Record<string, string> = {
  VALID: 'Riconosciuta',
  UNRECOGNIZED: 'Non riconosciuta',
  SKIPPED: 'Non verificata',
};

function formatDateIt(value: string): string {
  // Accetta `YYYY-MM-DD`; se il formato non matcha, restituisce il valore raw.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return value;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function CompletionConfirmationDialog({
  open,
  praticaShortId,
  clientName,
  protocolloAde,
  dataRegistrazione,
  firmaDigitaleStatus,
  isPending,
  onConfirm,
  onCancel,
}: CompletionConfirmationDialogProps) {
  const confirmButtonRef = React.useRef<HTMLButtonElement | null>(null);

  // Focus iniziale sul Conferma quando il dialog si apre.
  React.useEffect(() => {
    if (!open) return;
    // Microtask per permettere al DOM di renderizzare prima del focus.
    const t = window.setTimeout(() => {
      confirmButtonRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onCancel();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !isPending) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="completion-confirm-title"
        aria-describedby="completion-confirm-desc"
        className="w-full max-w-md rounded-lg border bg-background p-6 shadow-lg"
      >
        <header className="mb-4">
          <h2
            id="completion-confirm-title"
            className="text-lg font-semibold tracking-tight"
          >
            Conferma completamento pratica
          </h2>
          <p
            id="completion-confirm-desc"
            className="mt-1 text-sm text-muted-foreground"
          >
            La pratica passerà in stato <strong>COMPLETATA</strong> e il cliente
            riceverà un'email con il link alla ricevuta. L'azione non è
            reversibile.
          </p>
        </header>

        <dl className="mb-4 space-y-2 rounded-md border bg-muted/20 p-3 text-sm">
          <div className="flex justify-between gap-2">
            <dt className="font-medium text-muted-foreground">Pratica</dt>
            <dd className="font-mono">{praticaShortId}</dd>
          </div>
          {clientName && (
            <div className="flex justify-between gap-2">
              <dt className="font-medium text-muted-foreground">Cliente</dt>
              <dd>{clientName}</dd>
            </div>
          )}
          <div className="flex justify-between gap-2">
            <dt className="font-medium text-muted-foreground">Protocollo</dt>
            <dd className="font-mono">{protocolloAde}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="font-medium text-muted-foreground">
              Data registrazione
            </dt>
            <dd>{formatDateIt(dataRegistrazione)}</dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="font-medium text-muted-foreground">
              Firma digitale
            </dt>
            <dd>
              {FIRMA_LABELS[firmaDigitaleStatus] ?? firmaDigitaleStatus}
            </dd>
          </div>
        </dl>

        <footer className="flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isPending}
          >
            Annulla
          </Button>
          <Button
            ref={confirmButtonRef}
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            aria-disabled={isPending}
          >
            {isPending ? 'Completamento in corso…' : 'Conferma'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
